import type { CabinetTenantMembership, MyInvitation } from "@planerius/contracts/cabinet-auth"
import { useRouter } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useCallback, useEffect, useMemo, useState } from "react"

import { accessStore } from "../../access/access-store"
import { useAccessState } from "../../access/use-access"
import type { CabinetMemberSelf } from "../../api/auth.api"
import { fetchMemberSelf, listTenants, selectTenant } from "../../api/auth.api"
import { acceptMyInvitation, listMyInvitations } from "../../api/invitations.api"
import type { ApiProblem } from "../../api/problem"
import { isApiProblem, transportProblem } from "../../api/problem"
import { safeNextPath } from "../../app/navigate-next"
import { cx } from "../../lib/cx"
import { ProblemScreen, Skeleton } from "../../shell/states"
import { Badge, Button, Checkbox } from "../../ui"
import styles from "../auth.module.css"
import { accessLostTitle, type TenantRow, tenantRows } from "./choose-tenant-model"

/**
 * Выбор салона (1-Э1.4).
 *
 * Сервер не угадывает салон за человека: записать клиента не в тот салон хуже,
 * чем показать лишний экран. При единственном членстве этот экран не возникает вовсе —
 * сервер выбирает сам ещё на входе.
 *
 * Приглашения стоят здесь же, сверху: человек, которого позвали во второй салон, принимает
 * приглашение одной кнопкой, без ссылки из сообщения (сервер: invitations/accept-mine).
 * Согласия — те же два, что на экране по ссылке: вступление в салон без них не проходит.
 */

export function ChooseTenantScreen(props: { readonly next: string | undefined }): ReactNode {
  const router = useRouter()
  /*
   * ПОЧЕМУ ЧЕЛОВЕК ЗДЕСЬ ОКАЗАЛСЯ — ЭТО ГЛАВНОЕ, ЧТО НАДО СКАЗАТЬ.
   *
   * На этот экран приводят три разные дороги: салон не выбран (вошёл только что),
   * салон приостановлен и «вас убрали из салона». Третья приходит посреди рабочего дня,
   * и без объяснения человек видит список салонов вместо журнала и решает, что
   * приложение сломалось. Причина лежит в состоянии доступа — берём её оттуда, а не
   * догадываемся по списку.
   */
  const accessState = useAccessState()
  const reason = accessState.kind === "tenant_required" ? accessState.problem : null
  const [memberships, setMemberships] = useState<readonly CabinetTenantMembership[] | null>(null)
  const [invitations, setInvitations] = useState<readonly MyInvitation[]>([])
  const [self, setSelf] = useState<CabinetMemberSelf | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [entering, setEntering] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [accepting, setAccepting] = useState<{ readonly invitationId: string; readonly terms: boolean; readonly data: boolean } | null>(null)
  const [consentHint, setConsentHint] = useState(false)

  useEffect(() => {
    // Ошибку здесь не сбрасываем: список перечитывается и после неудачного принятия,
    // и её объяснение должно остаться на экране. Сбрасывает её ручной повтор.
    const controller = new AbortController()
    const run = async (): Promise<void> => {
      try {
        const [list, profile, mine] = await Promise.all([
          listTenants(controller.signal),
          // Подпись «вы вошли как …» — тоже дополнение, и сбой в ней не должен решать
          // судьбу всего экрана: без списка салонов человек не войдёт никуда, а без
          // подписи — войдёт. Ветка self === null на экране уже есть.
          fetchMemberSelf(controller.signal).catch(() => null),
          // Приглашения — дополнение к списку, а не его условие: сбой здесь не должен
          // закрывать человеку вход в салоны, где он уже работает.
          listMyInvitations(controller.signal).catch(() => ({ items: [] as MyInvitation[] })),
        ])
        if (controller.signal.aborted) return
        setMemberships(list)
        setSelf(profile)
        setInvitations(mine.items)
      } catch (error) {
        if (controller.signal.aborted) return
        setProblem(isApiProblem(error) ? error : transportProblem("unknown"))
      }
    }
    void run()
    return () => {
      controller.abort()
    }
  }, [attempt])

  const rows = useMemo(() => (memberships === null ? null : tenantRows(memberships, invitations)), [memberships, invitations])

  const openTenant = useCallback(
    async (tenantId: string): Promise<void> => {
      await selectTenant(tenantId)
      // Права, роль и филиалы поменялись целиком — профиль перечитывается заново.
      const state = await accessStore.reload()
      if (state.kind === "ready") {
        router.history.push(safeNextPath(props.next, "/"))
        return
      }
      if (state.kind === "failed") setProblem(state.problem)
    },
    [props.next, router],
  )

  const enter = useCallback(
    async (row: TenantRow): Promise<void> => {
      setEntering(row.key)
      setProblem(null)
      try {
        await openTenant(row.tenantId)
      } catch (error) {
        setProblem(isApiProblem(error) ? error : transportProblem("unknown"))
      } finally {
        setEntering(null)
      }
    },
    [openTenant],
  )

  const accept = useCallback(async (): Promise<void> => {
    if (accepting === null) return
    if (!accepting.terms || !accepting.data) {
      setConsentHint(true)
      return
    }
    setEntering(`i:${accepting.invitationId}`)
    setProblem(null)
    try {
      const { tenantId } = await acceptMyInvitation({
        invitationId: accepting.invitationId,
        consents: { terms: true, staffPersonalData: true },
      })
      await openTenant(tenantId)
    } catch (error) {
      setProblem(isApiProblem(error) ? error : transportProblem("unknown"))
      // Приглашение могли принять или отозвать в другой вкладке — список перечитывается.
      setAttempt((value) => value + 1)
    } finally {
      setEntering(null)
    }
  }, [accepting, openTenant])

  if (problem !== null && memberships === null) {
    return (
      <ProblemScreen
        problem={problem}
        onRetry={() => {
          setProblem(null)
          setAttempt((value) => value + 1)
        }}
      />
    )
  }

  const none = rows !== null && rows.length === 0

  return (
    <div className={styles.page}>
      <div className={cx(styles.card, styles.wide)}>
        <div className={styles.brand}>planerius</div>
        <h1 className={styles.title}>{none ? "Вы больше не состоите ни в одном салоне" : "Где сегодня работаете?"}</h1>
        <p className={styles.subtitle}>
          {none
            ? "Если это ошибка, обратитесь к владельцу салона."
            : self === null
              ? "Загружаем ваш профиль…"
              : `Вы вошли как ${self.displayName}. Одна учётная запись — разные салоны и роли.`}
        </p>

        {/*
          * Сначала причина, по которой человек здесь, и только потом ошибка его
          * последнего действия: перепутав порядок, мы ответим на вопрос, которого
          * он не задавал.
          */}
        {reason === null || problem !== null ? null : (
          <div className={styles.error} role="status">
            <div className={styles.errorTitle}>{accessLostTitle(reason)}</div>
            {/*
              * ПОЯСНЕНИЕ БЕРЁТСЯ ИЗ detail, А НЕ ИЗ message. message у нашего класса
              * ошибки — это «код: заголовок», то есть человек читал
              * «tenant_access_revoked: Доступ к салону отозван» и не узнавал того
              * единственного, что ему нужно: ИЗ КАКОГО салона его убрали. Название
              * салона сервер присылает, и оно лежит в detail и в поле tenantName.
              */}
            {reason.detail ?? "Выберите салон, в котором работаете."}
          </div>
        )}

        {problem === null ? null : (
          <div className={styles.error} role="alert">
            <div className={styles.errorTitle}>{problem.title}</div>
            {problem.detail ?? "Попробуйте ещё раз."}
          </div>
        )}

        {rows === null ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Skeleton width="100%" height={44} radius={8} />
            <Skeleton width="100%" height={44} radius={8} />
          </div>
        ) : (
          <ul className={styles.tenantList}>
            {rows.map((row) => (
              <li key={row.key}>
                {row.kind === "enter" ? (
                  <button
                    type="button"
                    className={styles.tenantButton}
                    disabled={entering !== null}
                    onClick={() => {
                      void enter(row)
                    }}
                  >
                    <RowHead row={row} />
                  </button>
                ) : row.kind === "invitation" ? (
                  <div className={styles.tenantInvite}>
                    <RowHead row={row} />
                    {row.note === null ? null : <span className={cx(styles.tenantNote, styles.tenantNoteWarn)}>{row.note}</span>}
                    {accepting?.invitationId === row.invitationId ? (
                      <div className={styles.tenantConsents}>
                        <Checkbox
                          checked={accepting.terms}
                          onChange={(checked) => setAccepting({ ...accepting, terms: checked })}
                        >
                          Принимаю условия использования
                        </Checkbox>
                        <Checkbox
                          checked={accepting.data}
                          onChange={(checked) => setAccepting({ ...accepting, data: checked })}
                        >
                          Согласен на обработку моих данных как сотрудника — отдельный документ
                        </Checkbox>
                        {consentHint && !(accepting.terms && accepting.data) ? (
                          <small className={styles.tenantNote}>Это два отдельных документа — отметьте оба.</small>
                        ) : null}
                        <Button
                          variant="primary"
                          block
                          loading={entering === row.key}
                          loadingLabel="Принимаем…"
                          onClick={() => void accept()}
                        >
                          Принять и открыть салон
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="primary"
                        block
                        disabled={entering !== null}
                        onClick={() => {
                          setConsentHint(false)
                          setAccepting({ invitationId: row.invitationId ?? "", terms: false, data: false })
                        }}
                      >
                        {row.action}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className={styles.tenantBlocked} aria-disabled="true">
                    <RowHead row={row} />
                    {row.note === null ? null : <span className={styles.tenantNote}>{row.note}</span>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        <p className={styles.footNote}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => {
              void accessStore.signOut()
            }}
          >
            Выйти
          </button>
        </p>
      </div>
    </div>
  )
}

function RowHead(props: { readonly row: TenantRow }): ReactNode {
  const { row } = props
  return (
    <span className={styles.tenantHead}>
      <span>
        <span className={styles.tenantName}>{row.name}</span>
        <br />
        <span className={styles.tenantRole}>{row.sub}</span>
      </span>
      {row.badge === null ? null : (
        <Badge className={styles.tenantBadgeSlot} tone={row.badge.tone}>
          {row.badge.text}
        </Badge>
      )}
    </span>
  )
}
